import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import { customersApi, type CustomerListQuery } from "../services/customersApi";
import { useNotification } from "@/shared/hooks/useNotification";
import { getErrorMessage } from "@/shared/utils/errorHandler";

interface ExportCustomersButtonProps {
  filters: Omit<CustomerListQuery, "pageNumber" | "pageSize">;
}

export const ExportCustomersButton = ({ filters }: ExportCustomersButtonProps) => {
  const [isExporting, setIsExporting] = useState(false);
  const notify = useNotification();

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const blob = await customersApi.exportCustomers(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `customers-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      notify.success("Customer list exported as CSV");
    } catch (error: unknown) {
      notify.error(getErrorMessage(error) || "Failed to export customers");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      variant="outline"
      className="gap-2"
      onClick={handleExport}
      disabled={isExporting}
    >
      {isExporting ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Download className="w-4 h-4" />
      )}
      {isExporting ? "Exporting..." : "Export CSV"}
    </Button>
  );
};
